import React from "react";
import type {
  ConfigurationVersions,
  InputSummarySection,
  PipeSpecification,
  ProcessSpecification,
  ProjectInformation,
} from "../models/types";

interface InputSpecificationSummaryProps {
  project: ProjectInformation;
  pipe: PipeSpecification;
  process: ProcessSpecification;
  inputSummary?: InputSummarySection[];
  versions?: ConfigurationVersions;
}

type DetailItem = { label: string; value: string | null };

const dash = (value: string | undefined) => (value?.trim() ? value.trim() : null);

const withUnit = (value: string | undefined, unit: string | undefined) => {
  const v = dash(value);
  if (!v) return null;
  return unit ? `${v} ${unit}` : v;
};

const isPlaceholder = (value: string | null | undefined) => {
  if (value == null) return true;
  const trimmed = value.trim();
  return !trimmed || trimmed === "—";
};

const DetailGroup = ({
  title,
  items,
}: {
  title: string;
  items: DetailItem[];
}) => {
  const visible = items.filter((item) => !isPlaceholder(item.value));
  if (!visible.length) return null;

  return (
    <section className="op-input-group">
      <h4 className="op-input-group__title">{title}</h4>
      <div className="op-input-group__grid">
        {visible.map((item) => (
          <div key={`${title}-${item.label}`} className="op-input-stat">
            <span className="op-input-stat__label">{item.label}</span>
            <span className="op-input-stat__value">{item.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

const SummaryCard = ({
  title,
  groups,
}: {
  title: string;
  groups: Array<{ title: string; items: DetailItem[] }>;
}) => {
  const visibleGroups = groups.filter((group) =>
    group.items.some((item) => !isPlaceholder(item.value)),
  );
  if (!visibleGroups.length) return null;

  return (
    <section className="op-form-card op-input-summary">
      <h3 className="op-input-summary__title">{title}</h3>
      <div className="op-input-summary__groups">
        {visibleGroups.map((group) => (
          <DetailGroup key={group.title} title={group.title} items={group.items} />
        ))}
      </div>
    </section>
  );
};

const sectionTitle = (section: string) => {
  switch (section.toLowerCase()) {
    case "project":
      return "Project information";
    case "pipe":
      return "Pipe specification";
    case "process":
      return "Process / coating";
    case "derived geometry":
      return "Derived geometry";
    default:
      return section;
  }
};

const isInputSection = (title: string) => {
  const key = title.toLowerCase();
  return (
    key.includes("project") ||
    key.includes("pipe") ||
    key.includes("process") ||
    key.includes("coating")
  );
};

const buildFallbackGroups = (
  project: ProjectInformation,
  pipe: PipeSpecification,
  process: ProcessSpecification,
): Array<{ title: string; items: DetailItem[] }> => [
  {
    title: "Project information",
    items: [
      { label: "Project number", value: dash(project.projectNumber) },
      { label: "Project name", value: dash(project.projectName) },
      { label: "Customer", value: dash(project.customer) },
      { label: "Engineer", value: dash(project.engineer) },
      { label: "Revision", value: dash(project.projectRevision) },
      { label: "Configuration date", value: dash(project.configurationDate) },
    ],
  },
  {
    title: "Pipe specification",
    items: [
      {
        label: "Outside diameter",
        value: withUnit(pipe.outsideDiameter, pipe.outsideDiameterUnit),
      },
      {
        label: "Wall thickness",
        value: withUnit(pipe.wallThickness, pipe.wallThicknessUnit),
      },
      { label: "Pipe grade", value: dash(pipe.pipeGrade) },
      { label: "Pipe length", value: withUnit(pipe.pipeLength, pipe.pipeLengthUnit) },
      { label: "Pipe tolerance", value: dash(pipe.pipeTolerance) },
      { label: "Pipe geometry", value: dash(pipe.pipeGeometry) },
      {
        label: "Cutback — long side",
        value: withUnit(pipe.cutbackLongSide, pipe.cutbackLongSideUnit),
      },
      {
        label: "Cutback — short side",
        value: withUnit(pipe.cutbackShortSide, pipe.cutbackShortSideUnit),
      },
      {
        label: "Chamfer angle",
        value: dash(pipe.chamferAngleDeg) ? `${pipe.chamferAngleDeg}°` : null,
      },
    ],
  },
  {
    title: "Process / coating",
    items: [
      { label: "Coating type", value: dash(process.coatingType) },
      {
        label: "Coating thickness",
        value: withUnit(process.coatingThickness, process.coatingThicknessUnit),
      },
      { label: "Heating requirement", value: dash(process.heatingRequirement) },
      { label: "Blast requirement", value: dash(process.blastRequirement) },
      { label: "Surface preparation", value: dash(process.surfacePreparation) },
    ],
  },
];

const InputSpecificationSummary = ({
  project,
  pipe,
  process,
  inputSummary,
  versions,
}: InputSpecificationSummaryProps) => {
  const groups =
    inputSummary && inputSummary.length > 0
      ? inputSummary.map((section) => ({
          title: sectionTitle(section.section),
          items: section.values.map((item) => ({
            label: item.label,
            value: item.value,
          })),
        }))
      : buildFallbackGroups(project, pipe, process);

  const inputGroups = groups.filter((group) => isInputSection(group.title));

  const versionItems: DetailItem[] = versions
    ? [
        { label: "Dataset revision", value: dash(versions.datasetRevision) },
        { label: "Blast Frame rule", value: dash(versions.blastFrameRuleVersion) },
        { label: "Induction Coil rule", value: dash(versions.inductionCoilRuleVersion) },
        {
          label: "Blast Frame length category",
          value: dash(versions.blastFrameLengthCategory),
        },
        {
          label: "Induction Coil length category",
          value: dash(versions.inductionCoilLengthCategory),
        },
      ]
    : [];

  return (
    <>
      <SummaryCard title="Input details" groups={inputGroups} />
      <SummaryCard title="Rule versions" groups={[{ title: "Rule versions", items: versionItems }]} />
    </>
  );
};

export default InputSpecificationSummary;
